import {
  Case,
  CaseAdmin,
  LISCase,
  Specimen,
  SpecimenAdmin,
  SpecimenProtocolOverride,
} from "pwapi";

import { LabSettings } from "pwapi/custom/LabSettings";

import { CaseDetailState } from "reducers/caseDetailReducer";

import joinWithCommas from "./joinWithCommas";

export const use_pw_source_attributes = (
  specimen: Specimen | Exclude<Case["specimens"], undefined>[0],
  _specimenCase?: CaseDetailState | CaseAdmin | Case
) => {
  // TODO: enable this _after_ a transitionary period
  // if (specimenCase?.source === Case.source.PW) return true;
  if (!specimen.source && specimen.type && specimen.site && specimen.procedure)
    return true;
  return false;
};

export const has_source_attributes = (
  specimen: Specimen,
  specimenCase?: CaseDetailState | CaseAdmin | Case
) => {
  return !!(use_pw_source_attributes(specimen, specimenCase)
    ? specimen.type && specimen.site && specimen.procedure
    : specimen.source);
};

export const specimen_source = (
  specimen: Specimen,
  specimenCase?: CaseDetailState | CaseAdmin | Case
) => {
  if (use_pw_source_attributes(specimen, specimenCase) && specimen.procedure)
    return [
      specimen.type,
      specimen.site?.trim().toUpperCase(),
      procedureEnumToLabel(specimen.procedure),
    ].join(", ");
  return specimen.source;
};

export const _compareSpecimens = (a: Specimen, b: Specimen): number =>
  a.name.localeCompare(b.name, "en", {
    numeric: true,
    sensitivity: "base",
  });

// DO NOT USE THIS FUNCTION DIRECTLY
//
// sortSpecimens() is a pure function with no memoization or caching built in.
// Instead, use functions from selectors/selectSortedCaseDetail.ts or hooks/useSortedCase.
// It is preferable to use the selectors when possible.
export const sortSpecimens = (specimens: Specimen[] = []): Specimen[] =>
  [...specimens].sort(_compareSpecimens);

const procedureLabels = {
  c: "CURETTINGS",
  e: "EXCISION",
  pe: "PUNCH EXCISION",
  ib: "INCISIONAL BIOPSY",
  nc: "NAIL CLIPPING",
  na: "NAIL AVULSION",
  pb: "PUNCH BIOPSY",
  sb: "SHAVE BIOPSY",
  sbd: "SHAVE BIOPSY AND DESTRUCTION",
  sr: "SHAVE REMOVAL",
  srd: "SHAVE REMOVAL AND DESTRUCTION",
  u: "UNKNOWN",
  snb: "SNIP BIOPSY",
  se: "STAGED EXCISION (SLOW MOHS)",
  gr: "GRADLE REMOVAL",
  ebx: "EXCISIONAL BIOPSY",
  fb: "FOREIGN BODY REMOVAL",
  mohs: "MOHS SURGERY",
  dblk: "DEBULK",
  o: "OTHER",
};
export const procedureEnumToLabel = (
  shortEnum: Specimen.procedure | LISCase["specimens"][0]["procedure"]
) => {
  return (shortEnum && procedureLabels[shortEnum]) || shortEnum;
};

export const protocolLabels: {
  [key in SpecimenProtocolOverride.protocol]: string;
} = {
  nail: "Nail",
  dif: "DIF",
  [SpecimenProtocolOverride.protocol.ALOPECIA]: "Alopecia",
  whole_specimen: "Whole Specimen",
  pash_reflex: "PAS-H Reflex",
  pasf_reflex: "PAS-F Reflex",
  [SpecimenProtocolOverride.protocol.SLOW_MOHS]: "Slow Mohs",
};

type ProtocolStatus = {
  active: boolean;
  reason: string;
};

type LISSpecimen = LISCase["specimens"][0];

// Protocol logic here is duplicated from the backend and is for display purposes only.
// The logic here should only be changed when copying changes from the backend.
// Backend src: /packages/backend/apps/histoslide/protocols.py

export const getProtocolStatus = (
  specimen: Specimen | LISSpecimen | SpecimenAdmin,
  protocol: SpecimenProtocolOverride.protocol,
  ignoreOverrides?: boolean,
  settings?: LabSettings
): ProtocolStatus => {
  const overrides = (specimen as SpecimenAdmin).protocol_overrides || [];
  if (
    !ignoreOverrides &&
    overrides.find((o) => o.protocol === protocol) !== undefined
  )
    return {
      active: !!overrides.find((o) => o.protocol === protocol)?.active,
      reason: "Manual override",
    };

  if (protocol === "nail") {
    const hasNailType = specimen.type === Specimen.type.NAIL;
    const hasNailCuttingProcedure =
      !!specimen.procedure &&
      [Specimen.procedure.NC, Specimen.procedure.NA].includes(
        specimen.procedure
      );
    const nail = hasNailType || hasNailCuttingProcedure;

    return {
      active: nail,
      reason: !nail
        ? "Specimen does not have Type = Nail or Procedure = Nail Cutting"
        : `Specimen has ${[
            hasNailType ? "Type = Nail" : null,
            hasNailCuttingProcedure ? "Procedure = Nail Cutting" : null,
          ]
            .filter((v) => !!v)
            .join(" and ")}`,
    };
  }

  if (protocol === "dif") {
    const dif = !!(specimen.clinical_history || "")
      .toLowerCase()
      .match(/\bdif\b/);

    return {
      active: dif,
      reason: !dif
        ? "Specimen does not have DIF keyword in its Clinical History"
        : "Specimen has DIF keyword in its Clinical History",
    };
  }

  if (protocol === SpecimenProtocolOverride.protocol.ALOPECIA) {
    const alopeciaKeywords = (settings?.alopecia_keywords?.active &&
      (settings?.alopecia_keywords?.details as { [key: string]: string[] })
        ?.positive) || [
      // "alopecia areata", // This is redundant after adding "alopecia"
      "aplasia cutis congenita",
      // "androgenetic alopecia", // This is redundant after adding "alopecia"
      // "central centrifugal cicatricial alopecia", // This is redundant after adding "alopecia"
      "dissecting cellulitis",
      "folliculitis decalvans",
      // "frontal fibrosing alopecia", // This is redundant after adding "alopecia"
      "female pattern baldness",
      "loose anagen syndrome",
      "lichen planopilaris",
      "telogen effluvium",
      "follicular degeneration syndrome",
      "trichotillomania",
      "hair loss",
      "hairloss",
      "alopecia", // This may be too general but all effects from the ALOPECIA Protocol are toggleable so it's okay for now
      // Initialisms
      "aa", // - Alopecia Areata
      "aga", // - Androgenetic Alopecia (male/female pattern alopecia)
      "ccca", // - Central Centrifugal Cicatricial Alopecia (Follicular Degeneration Syndrome)
      "dc", // - Dissecting Cellulitis
      "fd", // - Folliculitis Decalvans
      "ffa", // - Frontal Fibrosing Alopecia
      "fpb", // - Female Pattern Baldness
      "lahs", // - Loose Anagen Syndrome
      "lpp", // - Lichen Planopilaris
      "te", // - Telogen Effluvium
      "fds", // - Follicular Degeneration Syndrome
    ];
    const alopeciaNegativeKeywords = (settings?.alopecia_keywords?.active &&
      (settings?.alopecia_keywords?.details as { [key: string]: string[] })
        ?.negative) || ["NUB", "NMSC", "neoplasm of uncertain behavior"];

    const alopeciaPositiveRegex = new RegExp(
      `\\b(${alopeciaKeywords.join("|")})\\b`,
      "gi"
    );
    const alopeciaNegativeRegex = new RegExp(
      `\\b(${alopeciaNegativeKeywords.join("|")})\\b`,
      "gi"
    );
    const alopeciaMatches = alopeciaKeywords?.length
      ? specimen.clinical_history?.match(alopeciaPositiveRegex)
      : [];
    const alopeciaNegativeMatches = alopeciaNegativeKeywords?.length
      ? specimen.clinical_history?.match(alopeciaNegativeRegex)
      : [];

    const alopeciaMatchStrings = [
      ...new Set(alopeciaMatches?.map((s) => `"${s.toLowerCase()}"`)),
    ];
    const alopeciaNegativeMatchStrings = [
      ...new Set(alopeciaNegativeMatches?.map((s) => s)),
    ];

    if (
      alopeciaMatchStrings.length > 0 &&
      alopeciaNegativeMatchStrings.length === 0
    ) {
      return {
        active: true,
        reason: `Specimen has Alopecia keyword${
          alopeciaMatchStrings.length === 1 ? "" : "s"
        } ${joinWithCommas(alopeciaMatchStrings)} in its Clinical History`,
      };
    } else if (
      alopeciaMatchStrings.length > 0 &&
      alopeciaNegativeMatchStrings.length > 0
    ) {
      return {
        active: false,
        reason: `Specimen has negative Alopecia keyword${
          alopeciaNegativeMatchStrings.length === 1 ? "" : "s"
        } ${joinWithCommas(
          alopeciaNegativeMatchStrings
        )} in its Clinical History`,
      };
    }

    return {
      active: false,
      reason:
        "Specimen does not have Alopecia keywords in its Clinical History",
    };
  }

  if (protocol === "whole_specimen") {
    const wholeSpecimen =
      specimen.grossing_token_selections?.grossed &&
      !specimen.grossing_token_selections?.grossed?.value;

    return {
      active: wholeSpecimen,
      reason: wholeSpecimen
        ? "Specimen was grossed without keywords 'bisected', 'trisected', or 'serially sectioned'"
        : !specimen.grossing_token_selections?.grossed
        ? "Specimen has not been grossed"
        : `Specimen was grossed with keyword '${specimen.grossing_token_selections?.biopsy?.value}'`,
    };
  }

  if (protocol === SpecimenProtocolOverride.protocol.PASH_REFLEX) {
    if (!settings?.pash_reflex?.active)
      return {
        active: false,
        reason: "PAS-H Reflex is not enabled for this lab",
      };
    if (!specimen?.clinical_history)
      return {
        active: false,
        reason: "Specimen Has No Clinical History",
      };

    const positiveKeywords = (
      settings?.pash_reflex?.details as { [key: string]: string[] }
    )?.positive || [
      "allergic",
      "atopic",
      "candida",
      "carp",
      "cocci",
      "contact",
      "cutaneous t-cell lymphoma",
      "dermatitis",
      "dermatophytosis",
      "eczema",
      "erythema multiforme",
      "folliculitis",
      "fungus",
      "irritant",
      "intertrigo",
      "lupus",
      "majocchi's",
      "mycosis",
      "nummular",
      "onychomycosis",
      "periodic acid schiff stain",
      "pitiriass rubra pilaris",
      "psoriasis",
      "pityriasis rosea",
      "pustule",
      "sarcoid",
      "spongiotic",
      "scabies",
      "tinea",
      "valley fever",
    ];
    const positiveRegex = new RegExp(
      `\\b(${positiveKeywords.join("|")})\\b`,
      "i"
    );
    const positiveMatches = specimen.clinical_history?.match(positiveRegex);

    const negativeKeywords = (
      settings?.pash_reflex?.details as { [key: string]: string[] }
    )?.negative || [
      "carcinoma",
      "cyst",
      "bowen's",
      "fibroma",
      "keratosis",
      "lplk",
      "blk",
      "melanoma",
      "neoplasm",
      "nub",
      "tumor",
    ];
    const negativeMatches = negativeKeywords?.length
      ? specimen.clinical_history?.match(
          new RegExp(`\\b(${negativeKeywords.join("|")})\\b`, "i")
        )
      : [];

    return {
      active: !!positiveMatches?.length && !negativeMatches?.length,
      reason: !positiveMatches?.length
        ? "Specimen does not have PAS-H Reflex keywords in its Clinical History"
        : negativeMatches?.length
        ? "Specimen has negative PAS-H Reflex keywords in its Clinical History"
        : "Specimen has PAS-H Reflex keywords in its Clinical History",
    };
  }

  if (protocol === SpecimenProtocolOverride.protocol.PASF_REFLEX) {
    if (!settings?.pasf_reflex?.active)
      return {
        active: false,
        reason: "PAS-F Reflex is not enabled for this lab",
      };
    if (!specimen?.clinical_history)
      return {
        active: false,
        reason: "Specimen Has No Clinical History",
      };

    const positiveKeywords = (
      settings?.pasf_reflex?.details as { [key: string]: string[] }
    )?.positive || ["tinea"];
    const positiveRegex = new RegExp(
      `\\b(${positiveKeywords.join("|")})\\b`,
      "i"
    );
    const positiveMatches = specimen.clinical_history?.match(positiveRegex);

    const negativeKeywords =
      (settings?.pasf_reflex?.details as { [key: string]: string[] })
        ?.negative || [];
    const negativeMatches = negativeKeywords?.length
      ? specimen.clinical_history?.match(
          new RegExp(`\\b(${negativeKeywords.join("|")})\\b`, "i")
        )
      : [];

    return {
      active: !!positiveMatches?.length && !negativeMatches?.length,
      reason: !positiveMatches?.length
        ? "Specimen does not have PAS-F Reflex keywords in its Clinical History"
        : negativeMatches?.length
        ? "Specimen has negative PAS-F Reflex keywords in its Clinical History"
        : "Specimen has PAS-F Reflex keywords in its Clinical History",
    };
  }

  if (protocol === SpecimenProtocolOverride.protocol.SLOW_MOHS) {
    if (specimen.procedure === Specimen.procedure.SE)
      return {
        active: true,
        reason: `Specimen has Procedure: ${procedureLabels.se}`,
      };

    const keywords = ["staged excision", "enface", "en face", "mohs"];
    const regex = new RegExp(`\\b(${keywords.join("|")})\\b`, "gi");
    const matches = specimen.clinical_history?.match(regex);
    const keywordMatches = [
      ...new Set(matches?.map((s) => `"${s.toLowerCase()}"`)),
    ];

    if (keywordMatches.length > 0)
      return {
        active: true,
        reason: `Specimen has Slow Mohs keyword${
          keywordMatches.length === 1 ? "" : "s"
        } ${joinWithCommas(keywordMatches)} in its Clinical History`,
      };

    return {
      active: false,
      reason: `Specimen does not have Procedure: ${procedureLabels.se} or any Slow Mohs keywords in its Clinical History`,
    };
  }

  return {
    active: false,
    reason: "Not yet implemented",
  };
};

export const getProtocolStatuses = (
  specimen: Specimen | LISSpecimen,
  ignoreOverrides?: boolean,
  settings?: LabSettings
): { [key in SpecimenProtocolOverride.protocol]: ProtocolStatus } => ({
  alopecia: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.ALOPECIA,
    ignoreOverrides
  ),
  dif: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.DIF,
    ignoreOverrides
  ),
  nail: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.NAIL,
    ignoreOverrides
  ),
  whole_specimen: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.WHOLE_SPECIMEN,
    ignoreOverrides
  ),
  [SpecimenProtocolOverride.protocol.PASH_REFLEX]: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.PASH_REFLEX,
    ignoreOverrides,
    settings
  ),
  [SpecimenProtocolOverride.protocol.PASF_REFLEX]: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.PASF_REFLEX,
    ignoreOverrides,
    settings
  ),
  [SpecimenProtocolOverride.protocol.SLOW_MOHS]: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.SLOW_MOHS,
    ignoreOverrides
  ),
});

export const isBisected = (grossed: string | undefined) =>
  grossed === "bisected" ||
  grossed === "horizontally bisected" ||
  grossed === "vertically bisected";
